/**
 * How search reads what fans type — one module for the edge (functions/api/search.ts), the
 * site (src/data/events.ts) and the tests (scripts/search-helpers.test.mjs).
 *
 * Pure functions over words and rows: no database, no clock (today's date comes in as an
 * argument, in the VISITOR's time zone), so every rule here is tested on fixtures copied from
 * the live tables. The queries behind each rule are the audit's (2026-10-18): "cowboys" made the
 * Sugar Land Space Cowboys the best match, "knicks vs magic" lost the NBA game, "kniks" and
 * "msg" found nothing, "taylor swift" listed tribute bands with no word of warning, and
 * "comedy this weekend" opened a band called This Wild Life.
 *
 * PUBLIC FACTS ONLY: this file is bundled into the browser.
 */
import { slugify } from './slug.mjs';
import { LA28_MARK, PROTECTED_WORDS } from './legal.mjs';
import { FOREIGN_GAME_WORDS, FOREIGN_TICKET_WORDS, GAME_ALONE, PRICE_WORDS } from './search-words.mjs';

// ---- words --------------------------------------------------------------------------------

/**
 * A query's words, the way the full-text index cuts them: lower-case, letters and digits
 * (apostrophes kept inside a word: "guns n' roses"), "@" read as "at", at most 8 words.
 * @param {string | null | undefined} raw
 * @returns {string[]}
 */
export const tokenize = (raw) => {
  const words = String(raw ?? '')
    .toLowerCase()
    .replace(/@/g, ' at ')
    // " - " between two names is "vs" ("ajax - feyenoord"); a hyphen inside a word ("jay-z") is not
    .replace(/\s[-–—]\s/g, ' vs ')
    .replace(/["*(){}[\]:^~]/g, ' ')
    .split(/[^\p{L}\p{N}']+/u)
    .map((t) => t.replace(/^'+|'+$/g, ''))
    .filter((t) => t.length > 0);
  // "v" and "versus" between two names are "vs" too ("ajax v az"); at either end they are a word
  return words.map((t, i) => ((t === 'v' || t === 'versus') && i > 0 && i < words.length - 1 ? 'vs' : t)).slice(0, 8);
};

/** A name with spaces, punctuation and accents removed: "AC/DC" = "ac dc" = "acdc".
 * @param {string | null | undefined} s */
export const compact = (s) =>
  String(s ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '');

/**
 * Words people put in a ticket search that are never in the index. The haystack is name +
 * artist + teams + venue + city + category, so "tickets" appears nowhere — and since terms are
 * ANDed, leaving it in makes "ariana grande tickets" match nothing. "vs" is here too: SeatGeek
 * writes "Magic at Knicks", so "knicks vs magic" lost the NBA game to the G League's "vs.".
 * "schedule", "dates", "date" and "tix" are in no listing at all (D1, 2026-10-19): "knicks
 * schedule" and "metallica dates" answered "Nothing on the bill".
 */
export const FILLER = new Set([
  'tickets', 'ticket', 'concert', 'concerts', 'tour', 'tours', 'show', 'shows',
  'event', 'events', 'seat', 'seats', 'cheap', 'cheapest', 'buy', 'near',
  'price', 'prices', 'deal', 'deals', 'vs', 'schedule', 'dates', 'date', 'tix',
  // search-broad (2026-10-22, 730 fan queries): the ticket word in other languages…
  'kaarten', 'kaartjes', 'kopen', 'entradas', 'boletos', 'billets', 'biglietti', 'karten', 'eintrittskarten', 'ingressos',
  // …the question words around a price ("how much are knicks tickets")…
  'how', 'much', 'are', 'what', 'cost',
  // …the seat words no listing carries ("floor seats lakers", "vip metallica")…
  'floor', 'vip', 'courtside', 'best',
  // …and the other marketplaces ("knicks seatgeek", "knicks vivid seats"). Dropped only when another
  // name word remains (nameWords): "SeatGeek Stadium" and "Floor Jansen" still answer when typed
  // alone, and a page named by the words as typed ("Best Coast") is still found (queryShape `typed`).
  'stubhub', 'seatgeek', 'vivid', 'ticketmaster', 'tickpick', 'gametime', 'viagogo',
  // round 2 (2026-10-30): the other price words ("discount knicks tickets") and the plain ticket word of more languages
  // (shared/search-words.mjs): next to a name they ask for nothing the listings carry
  ...PRICE_WORDS, ...FOREIGN_TICKET_WORDS,
]);

/**
 * A year ("metallica 2026", "taylor swift tour 2026"): read like filler — two listings in the
 * index carry "2026", so ANDing it left Metallica's 61 rows out. A name that IS a year keeps it
 * ("The 1975", "1999"), and the exact-page lookup still tries the words with it ("Super Bowl
 * 2027", queryShape).
 */
export const YEAR = /^(19|20)\d\d$/;
/** Is words[i] a year the query adds (not one a name is made of: "The 1975")?
 * @param {string[]} words @param {number} i */
const addedYear = (words, i) => YEAR.test(words[i]) && !(i === 1 && words[0] === 'the');

/** The words between two clubs in a head-to-head ("knicks vs magic", "bears at lions"). Only
 *  "vs" is dropped everywhere: "at" is part of "Jazz at Lincoln Center". */
export const PAIR_WORDS = new Set(['vs', 'v', 'versus', 'at']);

/** Little words an intent reading skips ("concerts IN nyc", "comedy near ME"). */
const STOP = new Set(['in', 'at', 'the', 'this', 'near', 'me', 'for', 'on', 'around', 'a', 'an', 'of', 'to', 'my']);

/** "tickets FOR the lion king", "tix TO hamilton": the little word right after a ticket word asks
 *  for the name and is no part of it — every listing had to carry "for", so the search found
 *  nothing. Only after a ticket word: "For King & Country" and "To Kill a Mockingbird" are names.
 * @param {string[]} words @param {number} i */
const askingWord = (words, i) => ASKING.has(words[i]) && i > 0 && ASKED_AFTER.has(words[i - 1]);
const ASKING = new Set(['for', 'to', 'para', 'voor', 'für', 'fur', 'pour', 'per', 'de', 'du', 'di', 'van', 'des']);
const ASKED_AFTER = new Set([
  'tickets', 'ticket', 'tix', 'seats', 'seat',
  // …and the ticket word of other languages ("boletos para karol g", "kaarten voor ajax")
  ...FOREIGN_TICKET_WORDS,
]);

/** Can words[i] be part of a name: not a filler word, not a year the query adds, not the word that
 *  asks for the name ("tickets FOR ...")? @param {string[]} words @param {number} i */
export const isNameWord = (words, i) => !FILLER.has(words[i]) && !addedYear(words, i) && !askingWord(words, i);

/** A query's name words: what is left once the filler, the years it adds and the asking word
 *  after "tickets" are gone ("metallica 2026" → metallica, "the 1975 tour 2026" → the 1975,
 *  "tickets for the lion king" → the lion king). The filler (and years) themselves when nothing
 *  else is: a show called "The Show" still has something to match.
 * @param {string[]} words */
export const nameWords = (words) => {
  const kept = words.filter((w, i) => !FILLER.has(w) && !addedYear(words, i) && !askingWord(words, i));
  if (kept.some((w) => w !== 'the')) return kept;
  const noFiller = words.filter((w) => !FILLER.has(w));
  if (noFiller.length) return noFiller;
  // only filler: the words that ask for more than tickets ("seatgeek tickets" is SeatGeek, "cheap concert tickets" concerts)
  const noTicket = words.filter((w) => !TICKET_WORDS.has(w));
  return noTicket.length ? noTicket : words;
};

// ---- places -------------------------------------------------------------------------------

/** Common US city shorthand → the catalog's city name (one table for the edge and the site). */
export const CITY_ALIASES = {
  nyc: 'new york', ny: 'new york', la: 'los angeles', sf: 'san francisco',
  vegas: 'las vegas', philly: 'philadelphia', dc: 'washington', atl: 'atlanta',
  chi: 'chicago', nola: 'new orleans', htx: 'houston', bk: 'brooklyn',
};

/**
 * A whole query that names a place by a nickname → its list pages. "the garden" is Madison
 * Square Garden in New York and TD Garden in Boston, so both are offered; "the forum" is one
 * building listed under two names. Barclays and SoFi already resolve by prefix; naming them
 * here makes the page the best match instead of a chip.
 * @type {Record<string, string[]>}
 */
export const PLACE_PHRASES = {
  msg: ['venue:madison-square-garden'],
  'the garden': ['venue:madison-square-garden', 'venue:td-garden'],
  'the forum': ['venue:kia-forum', 'venue:the-kia-forum'],
  barclays: ['venue:barclays-center'],
  sofi: ['venue:sofi-stadium'],
  ...Object.fromEntries(Object.entries(CITY_ALIASES).map(([alias, city]) => [alias, [`city:${slugify(city)}`]])),
};

/**
 * A whole query that names a league page in other words: the college leagues' pages are
 * league:ncaa_football and league:ncaa_basketball ("NCAA Football"), so "college football"
 * found nothing at all; "march madness" is college basketball's tournament.
 * @type {Record<string, string[]>}
 */
export const LEAGUE_PHRASES = {
  'college football': ['league:ncaa_football'],
  'ncaa football': ['league:ncaa_football'],
  'college basketball': ['league:ncaa_basketball'],
  'ncaa basketball': ['league:ncaa_basketball'],
  'march madness': ['league:ncaa_basketball'],
};

// ---- genres and categories -----------------------------------------------------------------

/** @param {string[]} keys @param {string} hub @returns {[string, string][]} */
const syn = (keys, hub) => keys.map((k) => [compact(k), hub]);
/**
 * Words for a genre or category page → its hub. Only pages the menus show (scripts/hubs.mjs
 * writes genre pages with 8+ dates); compared without spaces or punctuation ("hip-hop" = "hip
 * hop", "r&b" = "rb"). A word that is also a club ("jazz", "magic") stays a club too: the
 * ranking below decides, and a tie is a "which one?".
 * @type {Record<string, string>}
 */
export const GENRE_SYNONYMS = Object.fromEntries([
  ...syn(['jazz', 'blues', 'jazz and blues', 'jazz & blues'], 'genre:music:jazz-and-blues'),
  ...syn(['hip hop', 'hiphop', 'rap'], 'genre:music:hip-hop'),
  ...syn(['r&b', 'rnb', 'r and b'], 'genre:music:r-and-b'),
  ...syn(['edm', 'electronic', 'techno'], 'genre:music:electronic'),
  ...syn(['country'], 'genre:music:country'),
  ...syn(['pop'], 'genre:music:pop'),
  ...syn(['latin', 'reggaeton'], 'genre:music:latin'),
  ...syn(['classical'], 'genre:music:classical'),
  ...syn(['k-pop', 'kpop'], 'genre:music:k-pop'),
  ...syn(['broadway'], 'genre:theater:broadway'),
  ...syn(['off broadway'], 'genre:theater:off-broadway'),
  ...syn(['west end'], 'genre:theater:west-end'),
  ...syn(['ballet', 'dance'], 'genre:theater:ballet-and-dance'),
  ...syn(['opera'], 'genre:theater:opera'),
  ...syn(['circus', 'cirque'], 'genre:theater:circus-and-cirque'),
  ...syn(['magic shows', 'magic show', 'magic'], 'genre:theater:magic'),
  ...syn(['musicals', 'musical'], 'genre:theater:musicals-and-plays'),
  ...syn(['comedy', 'comedian', 'comedians', 'stand up', 'standup'], 'cat:comedy'),
  ...syn(['concerts', 'concert', 'music', 'gigs', 'live music', 'konzert', 'konzerte', 'concerten', 'concierto', 'conciertos'], 'cat:music'),
  ...syn(['family', 'family shows', 'family show', 'kids shows'], 'genre:theater:family'),
  ...syn(['sports'], 'cat:sports'),
  ...syn(['theater', 'theatre', 'plays'], 'cat:theater'),
]);

/** A league named in a query that also asks for a time or a place: its page ("nfl this weekend"). */
const LEAGUE_WORDS = /** @type {Record<string, string>} */ ({
  nfl: 'league:nfl', nba: 'league:nba', nhl: 'league:nhl', mlb: 'league:mlb', mls: 'league:mls', wwe: 'league:wwe',
  'college football': 'league:ncaa_football', 'ncaa football': 'league:ncaa_football',
  'college basketball': 'league:ncaa_basketball', 'ncaa basketball': 'league:ncaa_basketball', 'march madness': 'league:ncaa_basketball',
});

/** Category words of an intent reading ("comedy this weekend", "concerts in nyc"). A "match" is a
 *  game too ("arsenal match december"): read as a name, the query found nothing. */
const CATEGORY_WORDS = {
  concert: 'music', concerts: 'music', music: 'music', gig: 'music', gigs: 'music',
  // search-broad: a concert in German, Dutch, Spanish and Italian. ("cabaret" is NOT comedy here: it is
  // the musical Cabaret, with a page of its own — review 2026-10-22)
  konzert: 'music', konzerte: 'music', concerten: 'music', concierto: 'music', conciertos: 'music', concerti: 'music',
  comedy: 'comedy', comedian: 'comedy', comedians: 'comedy', standup: 'comedy',
  sports: 'sports', game: 'sports', games: 'sports', match: 'sports', matches: 'sports',
  theater: 'theater', theatre: 'theater', musical: 'theater', musicals: 'theater', play: 'theater', plays: 'theater',
  // "wedstrijd", "spiel": a game, in Dutch, German and Spanish: the sports category like "match" (shared/search-words.mjs)
  ...Object.fromEntries(FOREIGN_GAME_WORDS.map((w) => [w, 'sports'])),
};

/**
 * A sport named by a word the listings do not carry ("voetbal", "fußball", "calcio", "soccer"): the
 * kinds of event it is (event.kind), which the intent reading filters a sports page's dates to.
 * "football" is soccer to a visitor outside the US and Canada (parseIntent `footballIsSoccer`) or
 * next to a soccer club (functions/api/search.ts), and gridiron for everyone else.
 * @type {Record<string, { name: string, kinds: string[] }>}
 */
export const SPORTS = {
  soccer: { name: 'soccer', kinds: ['soccer', 'mls'] },
};
/** @type {Record<string, keyof typeof SPORTS>} */
const SPORT_WORDS = {
  soccer: 'soccer', voetbal: 'soccer', fussball: 'soccer', 'fußball': 'soccer', futbol: 'soccer', 'fútbol': 'soccer',
  calcio: 'soccer', futebol: 'soccer',
};

/** The site path of a list page: cat:music → /music, genre:music:jazz-and-blues →
 *  /music/jazz-and-blues, artist:sombr → /artist/sombr.
 * @param {string} hub */
export const hubPath = (hub) => {
  const [kind, a, b] = hub.split(':');
  if (kind === 'cat') return `/${a}`;
  if (kind === 'genre') return `/${a}/${b}`;
  return `/${kind}/${a}`;
};

// ---- dates (always the visitor's) ----------------------------------------------------------

/**
 * Today's date where the visitor is (YYYY-MM-DD). The UTC date dropped every page whose next
 * show was tonight from 00:00 UTC — 8 pm in New York — until the next morning's build.
 * @param {string | null | undefined} timeZone  IANA zone (Cloudflare's request.cf.timezone)
 * @param {Date} [now]
 */
export const localDate = (timeZone, now = new Date()) => {
  for (const tz of [timeZone, 'America/New_York']) {
    if (!tz) continue;
    try {
      const s = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    } catch {
      /* an unknown zone: the next one */
    }
  }
  return new Date(now.getTime() - 5 * 3600e3).toISOString().slice(0, 10);
};

/** @param {string} date YYYY-MM-DD @param {number} days */
export const addDays = (date, days) => new Date(Date.parse(`${date}T12:00:00Z`) + days * 864e5).toISOString().slice(0, 10);

/**
 * This weekend, Friday to Sunday (from today when the weekend has begun) — the same rule as
 * the /this-weekend pages (src/data/hub-spec.ts weekendOf). `next`: the weekend after it.
 * @param {string} today @param {boolean} [next]
 */
export const weekendWindow = (today, next = false) => {
  const d = new Date(`${today}T12:00:00Z`);
  const dow = d.getUTCDay();
  const start = new Date(d);
  if (dow >= 1 && dow <= 4) start.setUTCDate(d.getUTCDate() + (5 - dow));
  const end = new Date(start);
  end.setUTCDate(start.getUTCDate() + (start.getUTCDay() === 0 ? 0 : 7 - start.getUTCDay()));
  const iso = (/** @type {Date} */ x) => x.toISOString().slice(0, 10);
  const w = { from: iso(start) < today ? today : iso(start), to: iso(end) };
  return next ? weekendWindow(addDays(w.to, 1)) : w;
};

/**
 * Holidays and the other time phrases people type, as the words the tokenizer cuts them into → what
 * they are. Longest first. "july 4th" is a day, not July; "new year's day" is Jan 1, not a year.
 * @type {[string[], string][]}
 */
const TIME_PHRASES = /** @type {[string[], string][]} */ ([
  ['christmas', 'christmas'], ['xmas', 'christmas'], ['thanksgiving', 'thanksgiving'], ['nye', 'new-years-eve'],
  ['new years eve', 'new-years-eve'], ["new year's eve", 'new-years-eve'], ['new years day', 'new-years-day'], ["new year's day", 'new-years-day'],
  ['christmas day', 'christmas'], ['christmas eve', 'christmas'], ['xmas day', 'christmas'], ['xmas eve', 'christmas'],
  ['thanksgiving day', 'thanksgiving'], ['thanksgiving eve', 'thanksgiving'], ['halloween night', 'halloween'],

  ['halloween', 'halloween'], ['july 4th', 'independence-day'], ['july 4', 'independence-day'], ['4th of july', 'independence-day'],
  ['fourth of july', 'independence-day'], ['independence day', 'independence-day'], ['memorial day', 'memorial-day'], ['labor day', 'labor-day'],
  ["valentine's", 'valentines'], ['valentines', 'valentines'], ["valentine's day", 'valentines'], ['valentines day', 'valentines'],
  ['this week', 'week'], ['next week', 'next-week'], ['opening night', 'opener'], ['home opener', 'opener'], ['opener', 'opener'],
].map(([phrase, name]) => /** @type {[string[], string]} */ ([phrase.split(' '), name])));

/** The time phrase that starts at words[i]: how many words it takes and what it is, or null.
 * @param {string[]} words @param {number} i
 * @returns {{ len: number, name: string } | null} */
const WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
/** "saturday", "next saturday": a weekday is the next one on or after today (a week later after "next"). */
TIME_PHRASES.push(...WEEKDAYS.flatMap((d, k) => /** @type {[string[], string][]} */ ([[[d], `wd:${k}`], [['next', d], `nwd:${k}`]])));

export const timePhraseAt = (words, i) => {
  let best = null;
  for (const [ws, name] of TIME_PHRASES) {
    if (ws.every((w, k) => words[i + k] === w) && (!best || ws.length > best.len)) best = { len: ws.length, name };
  }
  return best;
};

/** "oct concerts", "concerts dec": a month's short form is the month next to a kind of show (alone it is a name: Jan Lisiecki) */
const MONTH_ABBR = Object.assign(Object.create(null), { jan: 0, feb: 1, mar: 2, apr: 3, jun: 5, jul: 6, aug: 7, sep: 8, sept: 8, oct: 9, nov: 10, dec: 11 });
const MONTH_KIND_WORDS = new Set(['concerts', 'concert', 'shows', 'show', 'events', 'comedy', 'theater', 'theatre', 'sports', 'games', 'music', 'musicals', 'broadway']);
export const MONTHS = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];

/** The next occurrence of a month (this month counts): "december" on Oct 19, 2026 → 2026-12;
 *  with a year that is not past ("december 2027") that one.
 * @param {number} index 0 = January @param {string} today @param {number | null} [inYear] */
export const monthWindow = (index, today, inYear = null) => {
  const y = Number(today.slice(0, 4));
  const m = Number(today.slice(5, 7)) - 1;
  const next = index < m ? y + 1 : y;
  const year = inYear && (inYear > y || (inYear === y && index >= m)) ? inYear : next;
  const month = `${year}-${String(index + 1).padStart(2, '0')}`;
  const last = new Date(Date.UTC(year, index + 1, 0)).getUTCDate();
  const from = `${month}-01`;
  return { from: from < today ? today : from, to: `${month}-${String(last).padStart(2, '0')}`, month };
};

// ---- intent words --------------------------------------------------------------------------

/**
 * `said`: the time words as typed ("december 2027", "this weekend"), for "These match without …".
 * `holiday`: the day a holiday word names ("christmas", "thanksgiving", "nye": HOLIDAYS); `week`/`next-week`:
 * "this week", "next week"; `opener`: "opening night", "home opener" (no dates of their own: the club's next ones).
 * @typedef {{ kind: 'tonight' | 'today' | 'tomorrow' | 'weekend' | 'next-weekend' | 'month' | 'holiday' | 'week' | 'next-week' | 'opener' | 'weekday', weekday?: number, next?: boolean, month?: number, year?: number, holiday?: string, said?: string }} When
 * `typed[m - 1]`: the query's words up to the m-th leftover word, as typed — little words, genre,
 * category and filler words kept, the time, nickname and "near me" words left out (nameSlugs).
 * `sport`: a sport the words name ("voetbal", "soccer"; SPORTS), `footballIsSoccer` on parseIntent: "football" means it.
 * `price`: a price word was among them ("cheap", "deals", "discount", "last minute tickets"; shared/search-words.mjs PRICE_WORDS): no
 * intent of its own and no part of a name, but with nothing else in the query it is what priceAsk() answers.
 * @typedef {{ cat: string | null, genre: string | null, sport: keyof typeof SPORTS | null, when: When | null, near: boolean, cityAlias: string | null, rest: string[], typed: string[][], intent: boolean, price: boolean }} Intent
 */

/** "lakers home games", "jets away game": a club's games — the index carries "home" (Home Free)
 *  and "games", so the words matched nothing together. */
const GAME_PHRASES = new Set(['home game', 'home games', 'away game', 'away games', 'home match', 'home matches']);

/** A ticket word of any of the languages the search reads. */
const TICKET_ANY = new Set(['tickets', 'ticket', 'tix', ...FOREIGN_TICKET_WORDS]);
/**
 * Where "last minute" starts in the words, or -1: a price phrase only next to a ticket or price word ("last minute tickets",
 * "last minute knicks deals"); alone, or with a name ("Last Minute Man"), it is words of a title.
 * @param {string[]} words
 */
export const lastMinuteAt = (words) => {
  const at = words.findIndex((w, i) => w === 'last' && words[i + 1] === 'minute');
  return at >= 0 && words.some((w, i) => (i < at || i > at + 1) && (PRICE_WORDS.has(w) || TICKET_ANY.has(w))) ? at : -1;
};

/**
 * What a query asks for besides a name: a category or genre ("comedy", "jazz"), a time
 * ("tonight", "this weekend", "december", "december 2026"), a place ("nyc", "near me"); `rest`
 * is what is left (years are no name: "knicks december 2026" → knicks).
 * Read only AFTER the literal query found no exact page — "April Wine", "St. Louis Blues" and
 * "Tonight at the Improv" are names first (functions/api/search.ts).
 * @param {string[]} words  tokenize() output
 * @param {{ footballIsSoccer?: boolean }} [opts]
 * @returns {Intent}
 */
export const parseIntent = (words, opts = {}) => {
  /** @type {Intent} */
  const out = { cat: null, genre: null, sport: null, when: null, near: false, cityAlias: null, rest: [], typed: [], intent: false, price: false };
  /** @type {number | null} */
  let year = null;
  const lastMinute = lastMinuteAt(words);
  let yearAt = -1;
  /** where each leftover word is in `words` @type {number[]} */
  const restAt = [];
  /** the time, nickname and "near me" words: no part of a name @type {Set<number>} */
  const skip = new Set();
  /** where the time words are @type {number[]} */
  let whenAt = [];
  for (let i = 0; i < words.length; i++) {
    const w = words[i];
    const two = i + 1 < words.length ? `${w} ${words[i + 1]}` : '';
    // "last minute tickets": a price phrase, the two words are no name
    if (i === lastMinute) {
      out.price = true;
      skip.add(i).add(i + 1);
      i++;
      continue;
    }
    const phrase = timePhraseAt(words, i);
    if (phrase && !(phrase.name === 'opener' && words.length === phrase.len)) {
      const named = phrase.name === 'week' || phrase.name === 'next-week' || phrase.name === 'opener';
      const day = /^(n?)wd:(\d)$/.exec(phrase.name);
      out.when = day
        ? { kind: 'weekday', weekday: Number(day[2]), ...(day[1] ? { next: true } : {}) }
        : named ? { kind: phrase.name === 'week' ? 'week' : phrase.name === 'next-week' ? 'next-week' : 'opener' } : { kind: 'holiday', holiday: phrase.name };
      out.intent = true;
      // "memorial day WEEKEND", "christmas weekend": the weekend is part of the holiday
      const len = phrase.len + (!named && words[i + phrase.len] === 'weekend' ? 1 : 0);
      whenAt = Array.from({ length: len }, (_, k) => i + k);
      for (const k of whenAt) skip.add(k);
      i += len - 1;
      continue;
    }
    if (two === 'this weekend' || two === 'next weekend') {
      out.when = { kind: w === 'next' ? 'next-weekend' : 'weekend' };
      out.intent = true;
      whenAt = [i, i + 1];
      skip.add(i).add(i + 1);
      i++;
      continue;
    }
    if (two === 'near me' || two === 'around me') {
      out.near = true;
      out.intent = true;
      skip.add(i).add(i + 1);
      i++;
      continue;
    }
    if (GAME_PHRASES.has(two)) {
      out.cat = out.cat ?? 'sports';
      out.intent = true;
      skip.add(i).add(i + 1);
      i++;
      continue;
    }
    if (addedYear(words, i)) {
      if (year === null) [year, yearAt] = [Number(w), i];
      skip.add(i);
      continue;
    }
    // a league ("nfl tonight", "college football this weekend"): its page's dates, like a genre's
    const league = LEAGUE_WORDS[two] ?? LEAGUE_WORDS[w];
    if (league && !out.genre) {
      out.genre = league;
      out.intent = true;
      if (LEAGUE_WORDS[two]) i++;
      continue;
    }
    if (two && GENRE_SYNONYMS[compact(two)] && compact(two) !== compact(w)) {
      const hub = GENRE_SYNONYMS[compact(two)];
      if (hub.startsWith('cat:')) out.cat = out.cat ?? hub.slice(4);
      else out.genre = out.genre ?? hub;
      out.intent = true;
      i++;
      continue;
    }
    if (w === 'tonight' || w === 'today' || w === 'tomorrow' || w === 'weekend') {
      out.when = { kind: w };
      out.intent = true;
      whenAt = [i];
      skip.add(i);
    } else if (MONTHS.includes(w) || (MONTH_ABBR[w] !== undefined && words.length > 1 && words.some((x) => MONTH_KIND_WORDS.has(x)))) {
      out.when = { kind: 'month', month: MONTHS.includes(w) ? MONTHS.indexOf(w) : MONTH_ABBR[w] };
      out.intent = true;
      whenAt = [i];
      skip.add(i);
    } else if (SPORT_WORDS[w] || (w === 'football' && opts.footballIsSoccer)) {
      out.sport = out.sport ?? SPORT_WORDS[w] ?? 'soccer';
      out.intent = true;
    } else if (/** @type {Record<string, string>} */ (CATEGORY_WORDS)[w]) {
      out.cat = out.cat ?? /** @type {Record<string, string>} */ (CATEGORY_WORDS)[w];
      out.intent = true;
    } else if (GENRE_SYNONYMS[compact(w)]) {
      const hub = GENRE_SYNONYMS[compact(w)];
      if (hub.startsWith('cat:')) out.cat = out.cat ?? hub.slice(4);
      else out.genre = out.genre ?? hub;
      out.intent = true;
    } else if (/** @type {Record<string, string>} */ (CITY_ALIASES)[w] && !out.cityAlias) {
      const city = /** @type {Record<string, string>} */ (CITY_ALIASES)[w];
      out.cityAlias = slugify(city);
      out.intent = true;
      skip.add(i);
      // the city's own words right before its nickname are no name either: its whole name
      // ("washington dc" read "washington" as a club, "new york ny"), or its first word when the
      // nickname is its last ("comedy las vegas this weekend" kept "las" and answered nothing)
      const all = city.split(' ');
      const lead = city.endsWith(` ${w}`) ? all.slice(0, -1) : all;
      const from = restAt.length - lead.length;
      if (lead.length && from >= 0 && out.rest.slice(from).join(' ') === lead.join(' ') && restAt[from] === i - lead.length) {
        out.rest.splice(from);
        for (const at of restAt.splice(from)) skip.add(at);
      }
    } else if (w === 'nearby') {
      out.near = true;
      out.intent = true;
      skip.add(i);
    } else if (PRICE_WORDS.has(w)) {
      out.price = true;
    } else if (!STOP.has(w) && !FILLER.has(w)) {
      out.rest.push(w);
      restAt.push(i);
    }
  }
  // a genre that is also its category's word ("broadway" is theater) needs no category
  if (out.genre && out.cat && out.genre.startsWith(`genre:${out.cat}:`)) out.cat = null;
  // "december 2026": that year's December
  if (out.when?.kind === 'month' && year) {
    out.when.year = year;
    whenAt = [...whenAt, yearAt].sort((a, b) => a - b);
  }
  if (out.when) out.when.said = whenAt.map((k) => words[k]).join(' ');
  // the leftover words as typed, with the little words between them ("disney ON ice", "the book
  // OF mormon"), the genre, category and filler words among them ("phantom of the OPERA", "COMEDY
  // cellar", "CHEAP trick" are pages; "disney ice", "phantom", "cellar" are not)
  const kept = words.map((w, k) => (skip.has(k) ? '' : w));
  out.typed = out.rest.map((_, m) => kept.slice(0, m + 1 < restAt.length ? restAt[m + 1] : words.length).filter(Boolean));
  return out;
};

/**
 * A query that asks for a low price and nothing else ("cheap tickets", "deals", "discount tickets", "last minute tickets", "best
 * deals"): no show is named, so the answer is a neutral list page (PRICE_ROUTE), never a pick. With any other content ("cheap knicks
 * tickets") the price words are filler and the rest is searched. A time, a place or a kind of show is content ("cheap tickets near me").
 * @param {string[]} words  tokenize() output
 */
export const priceAsk = (words) => {
  const read = parseIntent(words);
  return read.price && !read.intent && read.rest.length === 0 && words.every((w) => PRICE_WORDS.has(w) || FILLER.has(w) || STOP.has(w) || w === 'last' || w === 'minute');
};

/**
 * "match", "wedstrijd", "spiel" with nothing else but ticket words: the game of no club in particular. Answered with the sports page
 * (GAME_ROUTE) unless a page of that very name is live (functions/api/search.ts).
 * @param {string[]} words  tokenize() output
 */
export const gameAsk = (words) => {
  const core = words.filter((w) => !FILLER.has(w) && !STOP.has(w));
  return core.length === 1 && GAME_ALONE.has(core[0]);
};

/** A little or filler word ("the", "of", "tickets"). @param {string} w */
const littleWord = (w) => STOP.has(w) || FILLER.has(w);
/** The last two words are a genre's or category's own ("magic shows", "live music").
 * @param {string[]} ws */
const genreEnd = (ws) => {
  const two = compact(ws.slice(-2).join(' '));
  return ws.length >= 2 && !!GENRE_SYNONYMS[two] && two !== compact(ws[ws.length - 1]);
};

/**
 * The page addresses a name's words can have: as typed ("disney on ice", "the book of mormon",
 * "lady a"), without the little and filler words at the end ("metallica tour") or in front
 * ("tickets for the lion king"), and without and with a leading "the" ("nutcracker" is The
 * Nutcracker's page; "the eagles" the band's `eagles`). A filler word that ends a genre's own
 * words stays ("orlando magic SHOWS" are magic shows in Orlando, not the Orlando Magic's games).
 * @param {string[]} words
 * @returns {string[]}
 */
export const nameSlugs = (words) => {
  const t = [...words];
  const typed = slugify(t.join(' '));
  while (t.length && littleWord(t[t.length - 1]) && !genreEnd(t)) t.pop();
  const asked = slugify(t.join(' '));
  while (t.length > 1 && t[0] !== 'the' && littleWord(t[0])) t.shift();
  const name = slugify(t.join(' '));
  const plain = name.replace(/^the-/, '');
  return [...new Set([typed, asked, name, plain, plain && `the-${plain}`])].filter(Boolean);
};

/**
 * A name's words without the category word that ends them, and the little words before it: "utah
 * jazz GAME" → utah jazz, "st louis blues GAMES" → st louis blues, "the book of mormon MUSICAL" →
 * the book of mormon. After the whole name such a word says what the page is, and the name as
 * typed WITH it ("utah-jazz-game") is no page. Null when no category word ends them, or only
 * little words would be left. A word that ends a genre's own words stays ("orlando live MUSIC",
 * as "orlando magic SHOWS" in nameSlugs). The page these words name must be that kind of show
 * (namesEvery: "orlando magic musical" is no Orlando Magic game) — functions/api/search.ts.
 * @param {string[]} words
 * @returns {string[] | null}
 */
export const bareName = (words) => {
  const t = [...words];
  let cut = false;
  while (t.length > 1 && !genreEnd(t)) {
    const w = t[t.length - 1];
    if (/** @type {Record<string, string>} */ (CATEGORY_WORDS)[w]) cut = true;
    else if (!littleWord(w)) break;
    t.pop();
  }
  return cut && !t.every(littleWord) ? t : null;
};

/**
 * Is every word typed for a page accounted for by the page itself: a word of its name or address
 * ("beyonce" is Beyoncé's), or a genre or category word for its own kind of show ("knicks GAME
 * tonight", "nate bargatze COMEDY", "hadestown BROADWAY")? The little and filler words aside
 * ("the", "of", "vs", "tickets"), and "play", the verb too ("when do the knicks PLAY", "metallica
 * PLAYS december"); the words run together count too ("acdc" is AC/DC). A reading of the leftover
 * words alone can spend a word of another name on a genre or a category, and then it is not:
 * "magic mike" read as magic shows plus a music act called "mike.", "jazz concerts utah" as jazz
 * plus the Utah Utes (a college club), "knicks vs magic" as the Knicks plus magic shows, "stars of
 * tomorrow - comedy" as comedy plus the Dallas Stars.
 * @param {{ name?: string | null, slug: string, kind?: string, cat?: string | null }} page  `cat`:
 *   the page's kind of show (hub_stat doc.cat: Music, Comedy, Sports, Theater)
 * @param {string[]} typed  the words typed for it (parseIntent `typed`)
 */
export const namesEvery = (page, typed) => {
  const own = new Set([...String(page.name ?? '').toLowerCase().split(/[^\p{L}\p{N}']+/u), ...page.slug.split('-')]);
  const kind = String(page.cat ?? (page.kind === 'team' ? 'sports' : '')).toLowerCase();
  const little = (/** @type {string} */ w) => STOP.has(w) || FILLER.has(w) || w === 'play' || w === 'plays';
  /** the kind of show a genre or category phrase names ("jazz" music, "game" sports) @param {string[]} ws */
  const kindOf = (ws) => {
    const hub = GENRE_SYNONYMS[compact(ws.join(' '))] ?? LEAGUE_WORDS[ws.join(' ')];
    if (hub) return hub.startsWith('league:') ? 'sports' : hub.split(':')[1];
    return ws.length === 1 ? (/** @type {Record<string, string>} */ (CATEGORY_WORDS)[ws[0]] ?? (SPORT_WORDS[ws[0]] ? 'sports' : '')) : '';
  };
  const ok = typed.map((w) => little(w) || own.has(w));
  for (let i = 0; i < typed.length; i++) {
    for (let n = 1; n <= 3 && i + n <= typed.length; n++) if (kind && kindOf(typed.slice(i, i + n)) === kind) ok.fill(true, i, i + n);
  }
  if (ok.every(Boolean)) return true;
  const joined = compact(typed.filter((w) => !little(w)).join(''));
  return !!joined && (joined === compact(page.name) || joined === compact(page.slug));
};

/** The nth (or, n = -1, the last) weekday of a month, as YYYY-MM-DD. @param {number} year @param {number} month 0 = January @param {number} weekday 0 = Sunday */
const nthWeekday = (year, month, weekday, n) => {
  if (n > 0) {
    const first = new Date(Date.UTC(year, month, 1)).getUTCDay();
    return new Date(Date.UTC(year, month, 1 + ((weekday - first + 7) % 7) + (n - 1) * 7)).toISOString().slice(0, 10);
  }
  const last = new Date(Date.UTC(year, month + 1, 0));
  return new Date(Date.UTC(year, month, last.getUTCDate() - ((last.getUTCDay() - weekday + 7) % 7))).toISOString().slice(0, 10);
};

/**
 * The days a holiday word means, the next time they are not past: Christmas Dec 24-26, Thanksgiving the
 * fourth Thursday of November (the US one), New Year's Eve Dec 31, New Year's Day Jan 1, Halloween Oct
 * 31, Independence Day Jul 4, Valentine's Feb 14, Memorial Day and Labor Day the long weekend (Saturday
 * to Monday). `from` is today when the days have begun.
 * @param {string} name  a TIME_PHRASES name @param {string} today YYYY-MM-DD
 * @returns {{ from: string, to: string } | null}
 */
export const holidayWindow = (name, today) => {
  const year = Number(today.slice(0, 4));
  /** @param {number} y */
  const days = (y) => {
    switch (name) {
      case 'christmas': return [`${y}-12-24`, `${y}-12-26`];
      case 'new-years-eve': return [`${y}-12-31`, `${y}-12-31`];
      case 'new-years-day': return [`${y}-01-01`, `${y}-01-01`];
      case 'halloween': return [`${y}-10-31`, `${y}-10-31`];
      case 'independence-day': return [`${y}-07-04`, `${y}-07-04`];
      case 'valentines': return [`${y}-02-14`, `${y}-02-14`];
      case 'thanksgiving': {
        const d = nthWeekday(y, 10, 4, 4);
        return [d, d];
      }
      case 'memorial-day': {
        const d = nthWeekday(y, 4, 1, -1);
        return [addDays(d, -2), d];
      }
      case 'labor-day': {
        const d = nthWeekday(y, 8, 1, 1);
        return [addDays(d, -2), d];
      }
      default: return null;
    }
  };
  for (const y of [year, year + 1]) {
    const w = days(y);
    if (w && w[1] >= today) return { from: w[0] < today ? today : w[0], to: w[1] };
  }
  return null;
};

/** "this week": today to Sunday; "next week": the Monday after to the Sunday. @param {string} today @param {boolean} [next] */
export const weekWindow = (today, next = false) => {
  const dow = new Date(`${today}T12:00:00Z`).getUTCDay();
  const sunday = addDays(today, (7 - dow) % 7);
  return next ? { from: addDays(sunday, 1), to: addDays(sunday, 7) } : { from: today, to: sunday };
};

/** The dates an intent's time words mean. @param {When | null} when @param {string} today */
export const windowOf = (when, today) => {
  if (!when) return null;
  if (when.kind === 'holiday') return when.holiday ? holidayWindow(when.holiday, today) : null;
  if (when.kind === 'weekday' && typeof when.weekday === 'number') {
    const dow = new Date(`${today}T12:00:00Z`).getUTCDay();
    const day = addDays(today, (when.weekday - dow + 7) % 7 + (when.next ? 7 : 0));
    return { from: day, to: day };
  }
  if (when.kind === 'week') return weekWindow(today);
  if (when.kind === 'next-week') return weekWindow(today, true);
  if (when.kind === 'opener') return null;
  if (when.kind === 'tonight' || when.kind === 'today') return { from: today, to: today };
  if (when.kind === 'tomorrow') return { from: addDays(today, 1), to: addDays(today, 1) };
  if (when.kind === 'weekend') return weekendWindow(today);
  if (when.kind === 'next-weekend') return weekendWindow(today, true);
  if (when.kind === 'month' && typeof when.month === 'number') return monthWindow(when.month, today, when.year ?? null);
  return null;
};

// ---- head-to-head ---------------------------------------------------------------------------

/**
 * `when`: the query's time words ("knicks vs magic december"), as parseIntent reads them.
 * @typedef {{ words: string[], clubs: string[] }} PairSide
 * @typedef {{ a: PairSide, b: PairSide, sep: boolean, when: When | null }} Pair
 */

/** The words for one game of a head-to-head ("lakers celtics GAME"): no club's name. */
const GAME_WORDS = new Set(['game', 'games', 'match', 'matches']);

/** Is words[i] one of the time words parseIntent reads ("tonight", "december", the "this" of
 *  "this weekend")? @param {string[]} words @param {number} i */
const timeWord = (words, i) => {
  const w = words[i];
  for (let k = Math.max(0, i - 3); k <= i; k++) {
    const phrase = timePhraseAt(words, k);
    if (phrase && k + phrase.len > i && phrase.name !== 'opener') return true;
  }
  if (w === 'tonight' || w === 'today' || w === 'tomorrow' || w === 'weekend' || MONTHS.includes(w)) return true;
  return (w === 'this' || w === 'next') && words[i + 1] === 'weekend';
};

/**
 * Two clubs in one query: "knicks vs magic", "bears at lions", "giants @ saints", or two (to
 * four) words that each name a club ("knicks magic", "red sox yankees") — unless the whole
 * query is one club's own name ("red sox", "maple leafs"). Each side keeps EVERY club its
 * name can mean ("giants": New York and San Francisco); the game decides.
 * Time words are no club's name: `when` is what they ask ("knicks vs magic december", "lakers
 * celtics tonight": the games in that window). Read as a name, "magic december" was no club, so
 * the query was the Knicks' December, or nothing at all ("lakers celtics december").
 * @param {string[]} words  tokenize() output
 * @param {(slug: string, sep?: boolean) => string[]} clubsFor  short name → full club slugs (functions/_lib/aliases.ts);
 *   `sep`: the name is on one side of a separator, where a school's bare name is a club too
 * @returns {Pair | null}
 */
export const parsePair = (words, clubsFor) => {
  // a side is a club's name: no filler, no year ("knicks vs magic 2026"), no "game"
  const named = (/** @type {string} */ x) => !FILLER.has(x) && !YEAR.test(x) && !GAME_WORDS.has(x);
  const side = (/** @type {string[]} */ ws, sep = false) => {
    const w = ws.filter(named);
    const plain = w[0] === 'the' ? w.slice(1) : w;
    return { words: plain, clubs: plain.length ? clubsFor(slugify(plain.join(' ')), sep) : [] };
  };
  const kept = words.filter((_, i) => !timeWord(words, i));
  const when = kept.length < words.length ? parseIntent(words).when : null;
  const at = kept.findIndex((w, i) => i > 0 && i < kept.length - 1 && PAIR_WORDS.has(w));
  if (at > 0) {
    const a = side(kept.slice(0, at), true);
    const b = side(kept.slice(at + 1), true);
    return a.clubs.length && b.clubs.length ? { a, b, sep: true, when } : null;
  }
  const ws = kept.filter(named);
  if (ws.length < 2 || ws.length > 4 || side(ws).clubs.length) return null;
  for (let k = 1; k < ws.length; k++) {
    const a = side(ws.slice(0, k));
    const b = side(ws.slice(k));
    if (a.clubs.length && b.clubs.length) return { a, b, sep: false, when };
  }
  return null;
};

// ---- list pages (hubs) ---------------------------------------------------------------------

/** Words that ask for tickets, not for anything in particular. */
const TICKET_WORDS = new Set(['tickets', 'ticket', 'tix', 'cheap', 'cheapest', 'buy', 'price', 'prices', 'deal', 'deals', 'best', ...PRICE_WORDS]);

/**
 * What a query's words name before any lookup: its address form (`s`, and without a leading
 * "the": "the eagles" is the band's page `eagles`), its spaces-and-punctuation-free form
 * ("kat seye" → katseye), the clubs and places its nickname means (`alias`), the genre or
 * category page its words are (`synonyms`, only for clients that know those pages' addresses)
 * and the exact page addresses to look up whatever their dates (`exactKeys`).
 * Also the same words with a city nickname spelled out (`slugs`) — "ny yankees" is the New
 * York Yankees' page, "sf giants" the San Francisco Giants' (read as a place, "ny" kept only the
 * games IN New York: none, the Bronx is its own city) — and the words as typed, with the filler
 * and years nameWords() sets aside (`typed`: "Cheap Trick", "The Rocky Horror Picture Show",
 * "Super Bowl 2027"); a page with that whole name is the one meant (rankHubs).
 * A head-to-head names its clubs, not one best page: no alias then.
 * @param {string[]} words  tokenize() output
 * @param {{ clubsFor: (slug: string) => string[], v2?: boolean, pair?: Pair | null }} opts
 */
export const queryShape = (words, { clubsFor, v2 = true, pair = null }) => {
  const names = nameWords(words);
  const s = slugify(names.join(' '));
  const sNoThe = names[0] === 'the' && names.length > 1 ? slugify(names.slice(1).join(' ')) : '';
  const compactQ = compact((sNoThe ? names.slice(1) : names).join(''));
  const phrase = names.join(' ');
  /** @type {Set<string>} */
  const alias = new Set();
  /** @type {Set<string>} */
  const synonyms = new Set();
  /** @type {Set<string>} the words with a city nickname spelled out */
  const spelled = new Set();
  /** @type {Set<string>} the words as typed */
  const typed = new Set();
  if (!pair && s) {
    for (const c of clubsFor(s)) alias.add(`team:${c}`);
    for (const k of [...(PLACE_PHRASES[phrase] ?? []), ...(LEAGUE_PHRASES[phrase] ?? [])]) alias.add(k);
    // ("cheap concert tickets": the ticket words around a kind of show name nothing)
    const bare = names.filter((w) => !TICKET_WORDS.has(w)).join(' ');
    const genre = v2 ? GENRE_SYNONYMS[compact(phrase)] ?? (bare && bare !== phrase ? GENRE_SYNONYMS[compact(bare)] : undefined) : undefined;
    if (genre) synonyms.add(genre);
    const city = /** @type {Record<string, string>} */ (CITY_ALIASES);
    if (names.length >= 2 && names.some((w) => city[w])) spelled.add(slugify(names.map((w) => city[w] ?? w).join(' ')));
    typed.add(slugify(words.filter((w) => !FILLER.has(w)).join(' ')));
    typed.add(slugify(words.join(' ')));
    for (const x of [s, sNoThe, '']) {
      spelled.delete(x);
      typed.delete(x);
    }
    for (const x of spelled) typed.delete(x);
  }
  /** @type {Set<string>} */
  const exactKeys = new Set(s ? ['artist', 'team', 'venue', 'city', 'league'].map((k) => `${k}:${s}`) : []);
  if (sNoThe) exactKeys.add(`artist:${sNoThe}`);
  if (compactQ.length >= 4 && compactQ !== s) for (const k of ['artist', 'team', 'venue']) exactKeys.add(`${k}:${compactQ}`);
  for (const x of [...spelled, ...typed]) for (const k of ['artist', 'team', 'venue']) exactKeys.add(`${k}:${x}`);
  return { names, s, sNoThe, compactQ, phrase, alias, synonyms, exactKeys, slugs: [s, sNoThe, ...spelled].filter(Boolean), typed: [...typed] };
};

/**
 * @typedef {{ hub: string, kind: string, slug: string, name: string | null, n: number | null, min_price: number | null,
 *   next_date: string | null, last_date: string | null, demand: number | null, league?: string | null, dkind?: string | null,
 *   cat?: string | null, venues?: number | null, topv?: string | null, cheapv?: string | null, vcity?: string | null }} HubCand
 * @typedef {HubCand & { group: number, exact: boolean, season: boolean }} RankedHub
 */

/** A page that has a date today or later (its stats row stays, at n = 0, when it empties).
 *  Genre and category pages only from 8 dates, as the menus.
 * @param {HubCand} h @param {string} today */
export const hubLive = (h, today) => (h.n ?? 0) > 0 && (h.last_date ?? '') >= today && ((h.kind !== 'genre' && h.kind !== 'cat') || (h.n ?? 0) >= 8);

/**
 * The pages a query names, best first: the clubs and places its nickname means (group 0), then
 * other exact names (group 1: the page's own name or address, with or without a leading "the",
 * spaces and punctuation aside — "kat seye" is KATSEYE), then the rest. Inside a group a page
 * in season (a date within 45 days) comes first, then demand, then how many dates — the date
 * count alone made the Sugar Land Space Cowboys (143 minor-league dates) beat Dallas.
 * A page named by the words exactly as typed (`typed`: "Super Bowl 2027", "Cheap Trick") is the
 * only exact one: the shorter reading ("Super Bowl", "Trick") is a chip.
 * @param {HubCand[]} rows
 * @param {{ slugs: string[], typed?: string[], compactQ: string, alias: Set<string>, synonyms: Set<string>, today: string }} ctx
 * @returns {RankedHub[]}
 */
export const rankHubs = (rows, ctx) => {
  const soon = addDays(ctx.today, 45);
  const typed = ctx.typed ?? [];
  const isTyped = (/** @type {HubCand} */ h) => typed.includes(h.slug) || typed.includes(slugify(h.name ?? ''));
  const named = (/** @type {HubCand} */ h) => {
    const name = h.name ?? '';
    if (ctx.slugs.includes(h.slug) || ctx.slugs.includes(slugify(name))) return true;
    if (ctx.compactQ.length < 4) return false;
    return compact(name) === ctx.compactQ || compact(name.replace(/^the\s+/i, '')) === ctx.compactQ || compact(h.slug) === ctx.compactQ;
  };
  const seen = new Set();
  /** @type {RankedHub[]} */
  const out = [];
  for (const h of rows) {
    if (seen.has(h.hub) || !hubLive(h, ctx.today)) continue;
    seen.add(h.hub);
    const group = ctx.alias.has(h.hub) ? 0 : named(h) || isTyped(h) || ctx.synonyms.has(h.hub) ? 1 : 2;
    out.push({ ...h, group, exact: group < 2, season: (h.next_date ?? '9999') <= soon });
  }
  if (typed.length && out.some(isTyped)) {
    for (const h of out) {
      if (isTyped(h)) continue;
      h.group = 2;
      h.exact = false;
    }
  }
  return out.sort(
    (a, b) => a.group - b.group || Number(b.season) - Number(a.season) || (b.demand ?? 0) - (a.demand ?? 0) || (b.n ?? 0) - (a.n ?? 0)
  );
};

/**
 * The best match, or the two or three pages a fan could equally mean. The top exact page is
 * the best match when it is the only one, or leads the next exact page by 8+ demand points;
 * otherwise the exact pages within 8 points of it are offered side by side ("eagles": the
 * Philadelphia Eagles and the band at Sphere) and none is called the best.
 * A nickname's club ranks first, unless a page with that very name is far more wanted: "dallas"
 * is the city (demand 93), not FC Dallas (68) — the club's lead made neither the best match
 * ("montreal", "kansas city" the same). One body's league page and team page ("WWE") are one.
 * @param {RankedHub[]} ranked  rankHubs() output
 * @returns {{ best: RankedHub | null, which: RankedHub[] }}
 */
export const pickBest = (ranked) => {
  const d = (/** @type {RankedHub} */ h) => h.demand ?? 0;
  const org = (/** @type {RankedHub} */ h) => h.kind === 'league' || h.kind === 'team';
  /** @type {RankedHub[]} */
  const exacts = [];
  for (const h of ranked) {
    if (!h.exact) continue;
    if (org(h) && exacts.some((x) => org(x) && compact(x.name) === compact(h.name))) continue;
    exacts.push(h);
  }
  if (!exacts.length) return { best: null, which: [] };
  let top = exacts[0];
  // (…not a page with a handful of dates over the club's 40: "syracuse orange" is the 43-date team page, not a one-date artist page)
  const wanted = exacts.find((h) => h.group > top.group && d(h) - d(top) >= 8 && (h.n ?? 0) * 4 >= (top.n ?? 0));
  if (wanted) top = wanted;
  const others = exacts.filter((h) => h !== top);
  if (!others.length || d(top) - Math.max(...others.map(d)) >= 8) return { best: top, which: [] };
  const which = [top, ...others].filter((h) => Math.abs(d(top) - d(h)) < 8).slice(0, 3);
  return { best: null, which: which.length >= 2 ? which : [] };
};

/**
 * The act, club or venue an intent query's words name ("knicks december", "giants sf"), among
 * the pages they can mean (`found`: live pages for the words, nickname clubs included): a club
 * named after the query's place first — "giants sf" is the San Francisco Giants, not New York's
 * — then a nickname's clubs, in season, by demand (the hub ranking). `local`: the place is part
 * of the page's own name, so it names the club and filters nothing (the New York Yankees play in
 * the Bronx, the Jets in East Rutherford: "IN New York" kept none of their games).
 * `entityChoices`: every page the words can mean, in that order (the server reads the first
 * three with the place and takes the first that has dates there: "the eagles las vegas" is the
 * band at Sphere, not the Philadelphia Eagles).
 * `slug` may list several addresses, the name as typed in its spellings: pages at those addresses
 * are equals, the more wanted first — the Orlando Magic's club page, not a one-date artist page
 * called "Orlando Magic"; The Nutcracker's 1,245 dates, not a one-date team page of that name.
 * @param {HubCand[]} found
 * @param {{ slug: string | string[] | null, place: string | null, today: string }} ctx  slug: the
 *   words' address (or addresses); place: the place page's slug
 * @returns {{ entity: RankedHub, local: boolean }[]}
 */
export const entityChoices = (found, { slug, place, today }) => {
  const slugs = [slug ?? []].flat().filter(Boolean);
  const named = place ? found.filter((h) => h.slug.startsWith(`${place}-`)) : [];
  const pool = named.length ? named : found;
  const alias = new Set(pool.filter((h) => h.kind === 'team' && !slugs.includes(h.slug)).map((h) => h.hub));
  return rankHubs(pool, { slugs, compactQ: '', alias, synonyms: new Set(), today }).map((entity) => ({
    entity,
    local: !!place && entity.slug.startsWith(`${place}-`),
  }));
};
/** The first of entityChoices(), or null.
 * @param {HubCand[]} found @param {{ slug: string | string[] | null, place: string | null, today: string }} ctx */
export const pickEntity = (found, ctx) => entityChoices(found, ctx)[0] ?? null;

const LEAGUE_TAG = /** @type {Record<string, string>} */ ({
  nfl: 'NFL', nba: 'NBA', nhl: 'NHL', mlb: 'MLB', mls: 'MLS', wwe: 'WWE', ncaa_football: 'College football', ncaa_basketball: 'College basketball',
});
const SPORT_TAG = /** @type {Record<string, string>} */ ({ hockey: 'Hockey', basketball: 'Basketball', soccer: 'Soccer', football: 'Football', baseball: 'Baseball', wrestling: 'Wrestling' });
const CAT_TAG = /** @type {Record<string, string>} */ ({ Music: 'Concerts', Comedy: 'Comedy', Theater: 'Theater', Sports: 'Sports' });
/** "new-york" → "New York" (a venue's city, stored as its page address) @param {string} slug */
const titleOf = (slug) => slug.split('-').map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w)).join(' ');

/**
 * What tells two same-name pages apart, from the page's own stats (hub_stat.doc): a club's
 * league (else its sport), an act's kind of show plus its venue when it plays three or fewer,
 * a genre page's category, a venue's city. `tag` is an English interface word the browser
 * translates (or a league's own abbreviation); `where` is a name.
 * @param {HubCand} h
 * @returns {{ tag: string | null, where: string | null }}
 */
export const hubLabel = (h) => {
  const tag = h.kind === 'team'
    ? LEAGUE_TAG[h.league ?? ''] ?? LEAGUE_TAG[h.dkind ?? ''] ?? SPORT_TAG[h.dkind ?? ''] ?? CAT_TAG[h.cat ?? ''] ?? null
    : h.kind === 'artist' || h.kind === 'genre'
      ? CAT_TAG[h.cat ?? ''] ?? null
      : null;
  const where =
    h.kind === 'artist' && (h.venues ?? 99) <= 3
      ? h.topv ?? h.cheapv ?? null
      : h.kind === 'venue' && h.vcity
        ? titleOf(h.vcity)
        : null;
  return { tag, where };
};

// ---- tribute and cover shows ---------------------------------------------------------------

/** How tribute and cover acts name themselves. */
export const TRIBUTE = /tribute|experience|candlelight|salute|celebrating|music of|legacy|dance night|impersonat|personnifie/i;

/** @typedef {{ name?: string | null, artist?: string | null, venue?: string | null, city?: string | null }} NamedRow */

/** Every word of a text (no 8-word cap: a venue and its city). @param {string | null | undefined} s */
const wordsIn = (s) =>
  String(s ?? '')
    .toLowerCase()
    .split(/[^\p{L}\p{N}']+/u)
    .map((t) => t.replace(/^'+|'+$/g, ''))
    .filter(Boolean);
/** The words of where a row is (its venue and city): "dallas" in "taylor swift dallas".
 * @param {NamedRow} r */
const placeWordsOf = (r) => new Set([...wordsIn(r.venue), ...wordsIn(r.city)]);

/** A row's act and title words. @param {NamedRow} r */
const nameWordsOf = (r) => new Set([...wordsIn(r.name), ...wordsIn(r.artist)]);
/** Does a row's act or title carry these words — run together ("acdc" in "AC/DC") or each as a
 *  whole word ("taylor swift eras" in "LOVER: The Unofficial Eras Tour - Taylor Swift Tribute")?
 * @param {NamedRow} r @param {string[]} act */
const carries = (r, act) => {
  if (compact(`${r.name ?? ''} ${r.artist ?? ''}`).includes(compact(act.join('')))) return true;
  const have = nameWordsOf(r);
  return act.every((w) => have.has(w));
};

/**
 * The share of rows that are tribute or cover shows (by act or title) OF THE ACT the query
 * names: with `words`, a row counts only when its act or title also carries the name, from the
 * words that are not where the row is ("taylor swift dallas": the Taylor Swift tribute at the
 * Dallas Arboretum) — "chris rock" matched one Chris Cornell tribute night in McKees Rocks,
 * which is nobody's Chris Rock tribute.
 * @param {NamedRow[]} rows @param {string[]} [words]
 */
export const tributeShare = (rows, words) => {
  if (!rows.length) return 0;
  return rows.filter((r) => {
    if (!TRIBUTE.test(`${r.name ?? ''} ${r.artist ?? ''}`)) return false;
    if (!words) return true;
    const here = placeWordsOf(r);
    const act = words.filter((w) => !here.has(w));
    return act.length > 0 && carries(r, act);
  }).length / rows.length;
};

/**
 * "No {name} dates are listed on TixInfo right now. These are tribute and cover shows.": the
 * act's name when at least half the rows are tribute or cover nights of it (tributeShare), the
 * act being the words that are not where most rows are ("taylor swift dallas" → Taylor Swift),
 * and its last word is a whole word of their names — "acd" is still being typed, and the
 * notice said "No Acd dates". Spelled the way the rows spell the longest run of those words
 * ("taylor swift eras" → Taylor Swift). Never for a query that asks for tribute shows. Null
 * otherwise. (functions/api/search.ts adds: no page of that act with dates, and no other act's
 * or club's page among the answer's pages.)
 * @param {NamedRow[]} rows @param {string[]} words  the query's name words (a leading "the" set
 *   aside: `the`, then tried first for the spelling — "The Cure", not "Cure")
 * @param {boolean} [the]
 * @returns {string | null}
 */
export const tributeName = (rows, words, the = false, typed = []) => {
  if (!rows.length || !words.length || TRIBUTE.test(words.join(' ')) || tributeShare(rows, words) < 0.5) return null;
  const act = words.filter((w) => rows.filter((r) => placeWordsOf(r).has(w)).length * 2 <= rows.length);
  if (!act.length) return null;
  const last = act[act.length - 1];
  if (!rows.some((r) => TRIBUTE.test(`${r.name ?? ''} ${r.artist ?? ''}`) && nameWordsOf(r).has(last))) return null;
  const withThe = the ? spelledIn(['the', ...act], rows) : null;
  if (withThe) return withThe;
  // the words as typed, filler and all, when a listing spells them that way ("best friends girl", "are you ready for it")
  for (let len = typed.length; len > act.length; len--) {
    for (let at = 0; at + len <= typed.length; at++) {
      const window = typed.slice(at, at + len);
      if (!act.every((w) => window.includes(w))) continue;
      const spelled = spelledIn(window, rows);
      if (spelled) return spelled;
    }
  }
  for (let len = act.length; len >= 1; len--) {
    for (let i = 0; i + len <= act.length; i++) {
      const spelled = spelledIn(act.slice(i, i + len), rows);
      if (spelled) return spelled;
    }
  }
  return nameFromRows(act, rows);
};

/**
 * How the rows spell the act the query names ("ac dc" → "AC/DC", "taylor swift" → "Taylor
 * Swift"), for "No AC/DC dates are listed…". The most common spelling wins; else the query
 * in title case.
 * @param {string[]} words @param {{ name?: string | null, artist?: string | null }[]} rows
 */
export const nameFromRows = (words, rows) => spelledIn(words, rows) ?? words.map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w)).join(' ');

/** The most common spelling of these words (run together, spaces and punctuation aside) in the
 *  rows' acts and titles, or null. @param {string[]} words @param {NamedRow[]} rows */
const spelledIn = (words, rows) => {
  const target = compact(words.join(''));
  const counts = new Map();
  if (target.length >= 2) {
    for (const r of rows.slice(0, 60)) {
      for (const text of [r.artist, r.name]) {
        if (!text) continue;
        const toks = [...String(text).matchAll(/[\p{L}\p{N}]+/gu)];
        for (let i = 0; i < toks.length; i++) {
          let acc = '';
          for (let j = i; j < toks.length && acc.length < target.length; j++) {
            acc += compact(toks[j][0]);
            if (acc === target) {
              const start = toks[i].index ?? 0;
              const end = (toks[j].index ?? 0) + toks[j][0].length;
              const s = String(text).slice(start, end);
              counts.set(s, (counts.get(s) ?? 0) + 1);
            }
          }
        }
      }
    }
  }
  let best = '';
  let n = 0;
  for (const [s, c] of counts) if (c > n || (c === n && /[A-Z]/.test(s) && !/[A-Z]/.test(best))) [best, n] = [s, c];
  return best || null;
};

// ---- typos ----------------------------------------------------------------------------------

/** Edits (insert, delete, substitute, swap two neighbours) between two strings.
 * @param {string} a @param {string} b */
export const damerau = (a, b) => {
  const n = a.length;
  const m = b.length;
  if (!n) return m;
  if (!m) return n;
  /** @type {number[][]} */
  const d = Array.from({ length: n + 1 }, (_, i) => [i, ...new Array(m).fill(0)]);
  for (let j = 0; j <= m; j++) d[0][j] = j;
  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
    }
  }
  return d[n][m];
};

/** How many edits a word of this length may need to still be "the same word": none up to 4
 *  letters ("bts", "msg", "cubs" are names, not typos), 1 up to 7, 2 from 8 — and suggest() lets
 *  the second edit through only from 10 letters on, on a word that starts with the same letter.
 * @param {number} len */
export const maxEdits = (len) => (len <= 4 ? 0 : len <= 7 ? 1 : 2);

/** Words a correction must never land on: filler, little words and intent words — "the
 *  weeknd" must never become "the weekend". */
const NEVER = new Set([...FILLER, ...STOP, 'tonight', 'today', 'tomorrow', 'weekend', 'next', ...MONTHS, ...Object.keys(CATEGORY_WORDS), ...Object.keys(SPORT_WORDS)]);
/** …and words a query uses as they are: the ticket word of another language ("billet" is not "ballet"), the words
 *  that ask for a time or a kind of show. A query word that is one of these is never corrected. */
const HELD = new Set([...NEVER].filter((w) => !STOP.has(w)).concat(['billet', 'biglietto', 'boleto', 'entrada', 'kaart', 'karte', 'bilhete', 'ticketing']));

/** A number a name spells in digits ("Formula 1", "Blink-182"): "one" is "1". */
const NUMBER_WORDS = /** @type {Record<string, string>} */ (Object.assign(Object.create(null), { one: '1', two: '2', three: '3', four: '4', five: '5', six: '6', seven: '7', eight: '8', nine: '9', ten: '10' }));
/** @param {string} w */
export const numberWord = (w) => NUMBER_WORDS[w] ?? w;
/** @param {string[]} ws */
const canon = (ws) => compact(ws.map(numberWord).join(''));
/** Is v the word w, or the same number in the other spelling?  @param {string} w @param {string} v */
const same = (w, v) => w === v || numberWord(w) === numberWord(v);

/** The tail words of a venue's name: "Feijenoord Stadion" is Feyenoord's ground, not another act. */
const VENUE_TAIL = new Set(['stadion', 'stadium', 'arena', 'park', 'field', 'centre', 'center', 'theatre', 'theater', 'hall', 'dome', 'garden', 'gardens']);

/**
 * @typedef {{ name: string, demand: number, ok: boolean, page?: string, nick?: boolean }} TypoCand
 * @typedef {{ text: string, name: string, distance: number, demand: number, of?: string, page?: string }} Suggestion
 * @typedef {{ nameW: string[], plainW: string[], nc: string, ncNoThe: string, pool: string[], spans: number[][], skip: boolean }} Prep
 */

/** What suggest() works out about a candidate name once (the build's name table is read by every query). @type {WeakMap<object, Prep>} */
const PREP = new WeakMap();
/** @param {TypoCand} c @returns {Prep} */
const prep = (c) => {
  let p = PREP.get(c);
  if (p) return p;
  // (the candidate's " - " is a separator, not a "vs": "The Greek Theatre - Los Angeles")
  const nameW = tokenize(c.name);
  const plainW = tokenize(c.name.replace(/\s[-–—]\s/g, ' '));
  p = {
    nameW,
    plainW,
    nc: canon(nameW),
    ncNoThe: canon(tokenize(c.name.replace(/^the\s+/i, ''))),
    // (the name's words, and two of them run together; " - " between them is no "vs")
    pool: [...plainW, ...plainW.slice(1).map((w, i) => plainW[i] + w)],
    spans: [...plainW.map((_, i) => [i, i]), ...plainW.slice(1).map((_, i) => [i, i + 1])],
    skip: !nameW.length || PROTECTED_WORDS.test(c.name) || LA28_MARK.test(c.name),
  };
  PREP.set(c, p);
  return p;
};

/**
 * The name a query with no results most likely misspells: compared with every candidate name
 * (list pages near it in the alphabet, the build's table of the most wanted names, the club and
 * place nicknames) as a whole (spaces and punctuation aside: "bruno marz" → Bruno Mars, "kat
 * seye" → KATSEYE) and word by word ("metalica" → Metallica), within maxEdits(). Only names
 * that have dates (`ok`); never onto a filler, little or intent word; never a protected word
 * (shared/legal.mjs); and not at all when two different corrections are within one edit and 10
 * demand points of each other (two names for one answer — a club and its ground — are not two).
 *
 * A word that is itself a name is not a typo (`opts.known`: words the index carries, `opts.vocab`:
 * words of the page names): from 5 letters on it is never changed, and a correction of the others
 * must keep it ("taylor swish" is not Taylor Swift). "one" and "1" are one word ("formula one").
 * @param {string[]} words  the query's name words
 * @param {TypoCand[]} cands
 * A query that IS a page's name ("new york jets" while the Jets have no date) is never corrected toward another ("Mets").
 * `opts.strictShort` (the search function): a word of 5 letters or fewer is corrected only toward a club's or place's
 * nickname ("kniks"), and a single word of 5 letters only the same way ("promo" is not Provo, "breda" not Brea).
 * @param {{ known?: Set<string>, vocab?: Set<string>, strictShort?: boolean }} [opts]
 * @returns {Suggestion | null}
 */
export const suggest = (words, cands, opts = {}) => {
  const qc = canon(words);
  if (qc.length < 3) return null;
  const qcNoThe = words[0] === 'the' ? canon(words.slice(1)) : qc;
  const isKnown = (/** @type {string} */ w) => !!(opts.known?.has(w) || opts.vocab?.has(w));
  const kept = words.filter((w) => (w.length >= 5 && isKnown(w)) || HELD.has(w));
  if (kept.length === words.length) return null;
  // the query is a page's name already (live or not): no other page is what it meant
  const said = words.join(' ');
  const saidNoThe = words[0] === 'the' ? words.slice(1).join(' ') : said;
  for (const c of cands) {
    if (!c.name) continue;
    const p = prep(c);
    if (p.skip || p.nc.length < 3) continue;
    const own = p.plainW.join(' ');
    if (own === said || own === saidNoThe || own.replace(/^the /, '') === saidNoThe) return null;
  }
  /** @type {Map<string, Suggestion>} */
  const options = new Map();
  const offer = (/** @type {Suggestion} */ s) => {
    const have = options.get(s.text);
    if (!have || s.distance < have.distance || (s.distance === have.distance && s.demand > have.demand)) options.set(s.text, s);
  };
  // …a word the query lacks may still be the one it MEANT: "theatr" for "theatre", "wickedmusical" run together (review 2026-10-23: a typo in a
  // name with a category or stop word in it was never corrected)
  /** the word sits (within one edit) inside the run-together query: "musical" in "wickedmusicl" */
  const inRun = (/** @type {string} */ w) => {
    if (words.length !== 1 || w.length < 5) return false;
    for (let n = w.length - 1; n <= w.length + 1; n++) for (let i = 0; i + n <= qc.length; i++) if (damerau(qc.slice(i, i + n), w) <= 1) return true;
    return false;
  };
  const lands = (/** @type {string[]} */ ws) =>
    ws.every(
      (w) =>
        !NEVER.has(w) ||
        words.includes(w) ||
        (words.length === 1 && w.length >= 4 && qc.includes(w)) ||
        inRun(w) ||
        (w.length >= 5 && words.some((qw) => qw.length >= 4 && qw !== w && damerau(qw, w) <= 1))
    );
  /** edits allowed between two words: the second only from 10 letters on, from the same first letter
   *  @param {string} a @param {string} b */
  const allowed = (a, b) => {
    const m = maxEdits(a.length);
    return m > 1 && !(a.length >= 10 && a[0] === b[0]) ? 1 : m;
  };
  // (a typo seldom changes the first letter: "preds" is not "reds", "tylor" is "taylor")
  const starts = (/** @type {string} */ a, /** @type {string} */ b) => a[0] === b[0] || (a[0] === b[1] && a[1] === b[0]);
  // the whole name takes two edits only for a single long word; several words have their own edits (below)
  const wholeEdits = (/** @type {string} */ a, /** @type {string} */ b) => (words.length > 1 ? Math.min(1, allowed(a, b)) : allowed(a, b));
  for (const c of cands) {
    if (!c.ok || !c.name) continue;
    const p = prep(c);
    if (p.skip) continue;
    const { plainW } = p;
    // the whole name, spaces and punctuation aside. An edit keeps every word the query is sure of and the name's own
    // number of words ("redsocks" is not "Red Rocks", "all in" is not "Allen"); only a name run together or split is the same name
    const keptOk = kept.every((w) => plainW.includes(w));
    for (const [q, target, qn, cn] of [
      [qc, p.nc, words.length, plainW.length],
      [qcNoThe, p.ncNoThe, words.length - (words[0] === 'the' ? 1 : 0), plainW.length - (plainW[0] === 'the' ? 1 : 0)],
    ]) {
      if (!target || Math.abs(target.length - q.length) > 2 || !starts(q, target) || q + 's' === target || target + 's' === q) continue;
      const dist = damerau(q, target);
      if (dist > 0 && (!keptOk || qn !== cn)) continue;
      if (dist > 0 && opts.strictShort && words.length === 1 && q.length <= 5 && !c.nick) continue;
      // …and no word of three letters or fewer changes ("nhl playoffs" is not "NFL Playoffs"): the words side by side
      if (dist > 0 && words.length > 1 && qn === cn) {
        const qw = q === qc ? words : words.slice(1);
        const nw = q === qc ? plainW : plainW.slice(plainW[0] === 'the' ? 1 : 0);
        if (qw.some((w, i) => w.length <= 3 && w !== nw[i] && !same(w, nw[i] ?? ''))) continue;
      }
      if (dist <= wholeEdits(q, target) && lands(plainW)) offer({ text: plainW.join(' '), name: c.name, of: c.name, distance: dist, demand: c.demand, page: c.page });
    }
    // word by word: every query word is one of the name's words (or two of them run together:
    // "acdc" in "Thunderstruck: AC/DC"), at least one of them corrected
    if (words.length > plainW.length + 2) continue;
    // (a short word is a word of a longer name only as itself: "elish" is not a word of "Renee Elise Goldsberry")
    // …and only of a name people look for: "flights" is not a word of "Island Fights", "henley" not of "Phil Hanley"
    if (words.length < plainW.length && (qc.length < 6 || (words.length === 1 && c.demand < 70))) continue;
    let total = 0;
    let changed = false;
    const fixed = [];
    // the name's words the query's words stand for, one after the other ("are you experienced" is no part of "Are You Ready For It? A Taylor Experience")
    let next = -1;
    let ordered = true;
    for (const w of words) {
      let best = w;
      let bd = Infinity;
      let at = -1;
      const strict = kept.includes(w);
      for (let k = 0; k < p.pool.length; k++) {
        const v = p.pool[k];
        if (same(w, v)) {
          [best, bd, at] = [v, 0, k];
          // (the word that follows the last one, if the name says it twice)
          if (next < 0 || p.spans[k][0] === next) break;
          continue;
        }
        if (strict || Math.abs(v.length - w.length) > 2 || (opts.strictShort && w.length <= 5 && !c.nick)) continue;
        // (a plural is another word, not a typo: "venues" is not "venue", "junior" is "juniors")
        if (!starts(w, v) || w + 's' === v || v + 's' === w) continue;
        // (two name words run together are matched as they are, never with a typo: "redsocks" is not "Red Rocks")
        if (k >= plainW.length) continue;
        const dist = damerau(w, v);
        if (dist < bd || (dist === bd && next >= 0 && p.spans[k][0] === next && p.spans[at]?.[0] !== next)) [best, bd, at] = [v, dist, k];
      }
      if (bd > allowed(w, best)) {
        total = Infinity;
        break;
      }
      if (at >= 0) {
        if (next >= 0 && p.spans[at][0] !== next) ordered = false;
        next = p.spans[at][1] + 1;
      }
      total += bd;
      if (best !== w) changed = true;
      fixed.push(best);
    }
    if (!ordered) total = Infinity;
    if (changed && Number.isFinite(total) && lands(fixed)) {
      // a joined name word ("acdc" from "ac" + "dc") is searched split, the way the index has it
      const text = fixed.map((w) => (plainW.includes(w) ? w : (() => {
        const i = plainW.findIndex((x, k) => k + 1 < plainW.length && x + plainW[k + 1] === w);
        return i >= 0 ? `${plainW[i]} ${plainW[i + 1]}` : w;
      })())).join(' ');
      offer({ text, name: text, of: c.name, distance: total, demand: c.demand, page: c.page });
    }
  }
  const ranked = [...options.values()].filter((s) => s.text !== words.join(' ')).sort((a, b) => a.distance - b.distance || b.demand - a.demand);
  const [best, second] = ranked;
  if (!best) return null;
  if (second && second.distance - best.distance <= 1 && best.demand - second.demand <= 10 && !oneAnswer(best, second)) return null;
  return best;
};

/**
 * Do two corrections lead to the same answer: a page of the same name with more after it
 * ("Cirque du Soleil - O"), the same page under two names (a club's nickname and its full name),
 * or a club and its ground ("Feyenoord", "Feijenoord Stadion")?
 * @param {Suggestion} best @param {Suggestion} second
 */
const oneAnswer = (best, second) => {
  const b = compact(best.text);
  const s = compact(second.text);
  if (s === b || s.startsWith(b)) return true;
  if (best.page && best.page === second.page) return true;
  // the ground of a name: "Feijenoord Stadion" without its tail word is "Feijenoord", a spelling of the club
  const groundOf = (/** @type {Suggestion} */ x) => {
    const tail = tokenize(x.of ?? x.name);
    let k = tail.length;
    while (k > 1 && VENUE_TAIL.has(tail[k - 1])) k--;
    return k === tail.length ? null : compact(tail.slice(0, k).join(' '));
  };
  const near = (/** @type {string | null} */ g, /** @type {string} */ other) => !!g && g[0] === other[0] && Math.min(g.length, other.length) >= 6 && damerau(g, other) <= 2;
  return near(groundOf(second), b) || near(groundOf(best), s);
};

/**
 * Other spellings of a query with (almost) no results, to try in the index: two words run
 * together ("kat seye" → "katseye") or one word split ("acdc" → "ac dc", searched as a phrase),
 * and a number in the other spelling ("formula one" → "formula 1").
 * @param {string[]} words
 * @returns {string[][]} each variant as search units (a unit with a space is a phrase)
 */
export const respellVariants = (words) => {
  /** @type {string[][]} */
  const out = [];
  // (the numbers first: the lookups are capped)
  const digits = words.map((w) => NUMBER_WORDS[w] ?? w);
  if (digits.some((w, i) => w !== words[i])) out.push(digits);
  const spelled = Object.assign(Object.create(null), Object.fromEntries(Object.entries(NUMBER_WORDS).map(([k, v]) => [v, k])));
  const letters = words.map((w) => spelled[w] ?? w);
  if (letters.some((w, i) => w !== words[i])) out.push(letters);
  for (let i = 0; i + 1 < words.length; i++) out.push([...words.slice(0, i), words[i] + words[i + 1], ...words.slice(i + 2)]);
  words.forEach((w, i) => {
    if (w.length < 4 || w.length > 8 || !/^[a-z]+$/.test(w)) return;
    for (let k = 2; k < w.length; k++) out.push([...words.slice(0, i), `${w.slice(0, k)} ${w.slice(k)}`, ...words.slice(i + 1)]);
  });
  return out.slice(0, 10);
};

/**
 * A query with one word nothing in the index carries ("taylor swift eras chicago" when no
 * listing says "eras", "knicks presale"): that word, to search without — only when exactly one
 * word is missing. Two-word queries take the answer only when the word left names a page
 * exactly (functions/api/search.ts): "billie eilish" must not become Billie Jean King Cup.
 * @param {string[]} words @param {boolean[]} present  per word: does the index carry it
 * @returns {number} the index of the word to drop, or -1
 */
export const partialDrop = (words, present) => {
  if (words.length < 2) return -1;
  const missing = present.map((p, i) => (p ? -1 : i)).filter((i) => i >= 0);
  return missing.length === 1 ? missing[0] : -1;
};

// ---- junk: words that name nothing ---------------------------------------------------------------

/**
 * search-broad R11. Of 52 junk queries ("the", "and", "game", "arena", "2026", "my tickets", "help",
 * "refund", "cancelled", "test") 22 returned 100 random rows (2026-10-19): a single word got a prefix
 * wildcard and the page cards of every look-alike. These rules apply to a SETTLED query (the search
 * page: submitted, `&s=1`), never to type-ahead, where "kni" must still find the Knicks.
 */

/** Words people type around a question that name nothing ("anything", "please"). */
const CHATTER = new Set([
  // pronouns and function words ("that", "your", "they"): no show is named by one of them alone
  'that', 'these', 'those', 'they', 'he', 'she', 'your', 'our', 'mine', 'yours', 'their', 'nowhere', 'somewhere', 'everywhere',
  'anything', 'something', 'everything', 'please', 'pls', 'plz', 'thanks', 'thank', 'you', 'any', 'some', 'good', 'nice', 'great', 'cool', 'fun',
  'i', 'we', 'my', 'me', 'can', 'could', 'do', 'does', 'did', 'is', 'am', 'was', 'it', 'its', 'get', 'got', 'want', 'wanna', 'need', 'looking',
  'find', 'show', 'where', 'when', 'which', 'who', 'whats', "what's", 'there', 'here', 'about', 'with', 'from', 'and', 'or', 'so', 'if', 'all',
  'some', 'something', 'new', 'last', 'minute', 'free', 'online', 'now', 'today', 'upcoming',
]);
/** Everyday words no show is named after — typed into a ticket search by people who want something else. */
const NON_NAMES = new Set([
  'hello', 'hi', 'hey', 'test', 'testing', 'weather', 'pizza', 'food', 'restaurant', 'restaurants', 'parking', 'hotel', 'hotels', 'flights', 'flight', 'airport',
  'taxi', 'uber', 'lyft', 'wheelchair', 'accessible', 'accessibility', 'directions', 'map', 'maps', 'address', 'hours', 'wifi', 'password', 'email',
  'phone', 'bitcoin', 'coupon', 'coupons', 'promo', 'discount', 'code', 'gift', 'lunch', 'dinner', 'breakfast', 'coffee', 'beer', 'wine', 'shopping', 'store', 'chart', 'seating',
]);
/** Marketplace and seat words are filler next to a name, and a name by themselves ("SeatGeek
 *  Stadium", "Floor Jansen", "Vivid Music Hall"). */
const NAMEABLE = new Set(['stubhub', 'seatgeek', 'vivid', 'ticketmaster', 'tickpick', 'gametime', 'viagogo', 'floor']);
/** Words of a place or a kind of show that name no one in particular. */
const GENERIC = new Set(['game', 'games', 'match', 'matches', 'arena', 'stadium', 'center', 'centre', 'theatre', 'theater', 'hall', 'park', 'field', 'club', 'venue', 'venues']);

/**
 * Does a query carry a word that can name something: not a filler, little, chatter or generic place
 * word, not a time word, not a year near this one? ("the best cheap tickets for anything near me"
 * carries none: it is a sentence; "1975" is a band.)
 * @param {string[]} words @param {number} year  this year, the visitor's
 */
export const hasNameWord = (words, year) =>
  words.some((w, i) => NAMEABLE.has(w) || !(STOP.has(w) || FILLER.has(w) || CHATTER.has(w) || NON_NAMES.has(w) || GENERIC.has(w) || (YEAR.test(w) && Math.abs(Number(w) - year) <= 2) || askingWord(words, i) || MONTHS.includes(w) || w === 'tonight' || w === 'tomorrow' || w === 'weekend' || w === 'week'));

/**
 * A settled query of stop words only ("the", "and", "vs", "game", "arena", "tickets", a bare year,
 * "the best cheap tickets for anything near me"): answered with the hint to type a name, no rows.
 * What reads as a time or a place ("tonight", "near me", "this weekend", "december", "concerts") is
 * an intent, not a stop word, and still answers.
 * @param {string[]} words @param {string} today  YYYY-MM-DD, the visitor's: a bare year near it is junk, "1975" is a band
 */
export const stopOnly = (words, today) => {
  if (!words.length || hasNameWord(words, Number(today.slice(0, 4)))) return false;
  // "game"/"match" read as the sports category on their own; with nothing else they name nothing
  // …and the little words of a sentence around a time or a place ("whats on tonight", "show me concerts") are no part of it
  const probe = words.filter((w) => w === 'near' || w === 'me' || w === 'around' || (!GAME_WORDS.has(w) && (!(CHATTER.has(w) || STOP.has(w)) || !!parseIntent([w]).intent)));
  return !parseIntent(probe).intent;
};

/**
 * Support questions ("help", "refund", "login", "my tickets", "order status", "customer service",
 * "cancelled"): TixInfo compares prices, the marketplace the fan bought from handles the order — a
 * help card, no rows. Whole phrases only; "where is my order" and the like.
 * @param {string[]} words
 */
export const supportAsk = (words) => {
  const q = words.filter((w) => w !== 'tickets' && w !== 'ticket').join(' ');
  const raw = words.join(' ');
  return SUPPORT.has(q) || SUPPORT.has(raw) || /^(how (do|can) i )?(get|ask for|request) (a )?refund$/.test(q) || /^(where is|track|check) my (order|tickets?)$/.test(raw);
};
const SUPPORT = new Set([
  'help', 'support', 'refund', 'refunds', 'login', 'log in', 'sign in', 'signin', 'my order', 'my orders', 'order status', 'customer service',
  'customer support', 'contact us', 'cancelled', 'canceled', 'cancel order', 'my account', 'my tickets', 'my ticket', 'phone number',
  'how do i get a refund', 'how to get a refund',
]);

/** A partner's test listing ("Test venue Liverpool", "GROUP SALES: Test" at "Visibility Test
 *  Stadium"): never a result. @param {{ name?: string | null, venue?: string | null }} r */
export const isTestRow = (r) => /test venue|visibility test/i.test(r.venue ?? '') || /^group sales:\s*test\b/i.test(r.name ?? '');

/**
 * A list page found only by the look of its address ("habs" → Habstrakt, "ucl" → UCLA, "bolts" → The
 * Dead Bolts) is worth a card only when its name STARTS WITH THE WHOLE WORDS typed (the last one may
 * be a prefix of a word of 5+ letters: "lambeau" → Lambeau Field, "cheap tr" no) and it is at least
 * as wanted as the best row whose title carries the words.
 * @param {{ name?: string | null, demand?: number | null }} h @param {string[]} words @param {number} bestDemand
 */
export const rangeCardOk = (h, words, bestDemand) => {
  const name = tokenize(h.name).filter((w, i) => !(i === 0 && w === 'the' && words[0] !== 'the'));
  const starts = words.length > 0 && words.length <= name.length && words.every((w, i) => name[i] === w || (i === words.length - 1 && (w.length >= 5 || (words.length >= 2 && w.length >= 3)) && name[i].startsWith(w)));
  return starts && (h.demand ?? 0) >= bestDemand;
};

// ---- the postseason --------------------------------------------------------------------------

/** Time words around a round ("world series tonight", "alcs this weekend"): the games are listed in date order anyway. */
const POSTSEASON_TIME = new Set(['today', 'tonight', 'tomorrow', 'weekend', 'this', 'next', 'week', 'now']);

/** "game six": a game's number in words (a series has at most seven). */
const GAME_NUMBERS = /** @type {Record<string, number>} */ ({ one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7 });

/**
 * @typedef {{ round: 'world series' | 'alcs' | 'nlcs', game: number | null, said: string | null }} PostseasonAsk
 * `game`: the game the query names ("world series game 6"), `said` how it was typed ("game 6").
 */

/**
 * "world series", "fall classic", "alcs", "nlcs" (with or without "tickets", "games", "mlb", "the"
 * and a year, in any order): the MLB round they mean, searched as that phrase among MLB games only
 * — the College World Series (June, Omaha) answers "college world series". "ws" only next to
 * another of those words ("ws tickets", "ws 2026", "ws game 3"): alone it is two letters that also
 * mean WSU. A game's number ("game 6", "gm 6", "g6", "game six", "game 7 world series") is the
 * game asked for: "world series game 6" listed the NCAA College World Series' Game 6.
 * @param {string[]} words  tokenize() output
 * @returns {PostseasonAsk | null}
 */
export const postseasonAsk = (words) => {
  /** @type {number | null} */
  let game = null;
  /** @type {string | null} */
  let said = null;
  const rest = [];
  let around = 0;
  for (let i = 0; i < words.length; i++) {
    const w = words[i];
    const joined = w.match(/^(?:game|gm|g)(\d{1,2})$/);
    const next = words[i + 1];
    const num = (w === 'game' || w === 'gm') && next !== undefined ? (/^\d{1,2}$/.test(next) ? Number(next) : GAME_NUMBERS[next] ?? null) : null;
    if (joined || num !== null) {
      // one game at a time ("game 6 game 7" is no question we can answer)
      if (game !== null) return null;
      game = joined ? Number(joined[1]) : /** @type {number} */ (num);
      said = joined ? w : `${w} ${next}`;
      around++;
      if (!joined) i++;
      continue;
    }
    if (FILLER.has(w) || YEAR.test(w) || w === 'mlb' || w === 'the' || w === 'game' || w === 'games' || POSTSEASON_TIME.has(w)) {
      around++;
      continue;
    }
    rest.push(w);
  }
  const r = rest.join(' ');
  /** @type {PostseasonAsk['round'] | null} */
  const round =
    r === 'world series' || r === 'fall classic' || (r === 'ws' && around > 0)
      ? 'world series'
      : r === 'alcs' || r === 'al championship series' || r === 'american league championship series'
        ? 'alcs'
        : r === 'nlcs' || r === 'nl championship series' || r === 'national league championship series'
          ? 'nlcs'
          : null;
  return round ? { round, game, said } : null;
};

/**
 * A club with the round it plays ("guardians world series", "rays alcs"): the words without the round,
 * for the club's own search — an MLB club in the postseason lists its postseason games first
 * (functions/api/search.ts). Null when no other name word remains, or it is the College World Series.
 * @param {string[]} words
 * @returns {string[] | null}
 */
export const withoutRound = (words) => {
  if (words.includes('college') || words.includes('ncaa')) return null;
  const rest = [];
  let found = false;
  for (let i = 0; i < words.length; i++) {
    const two = `${words[i]} ${words[i + 1] ?? ''}`;
    if (two === 'world series' || two === 'fall classic') {
      found = true;
      i++;
    } else if (words[i] === 'alcs' || words[i] === 'nlcs') found = true;
    else rest.push(words[i]);
  }
  return found && rest.some((w, i) => isNameWord(rest, i) && !POSTSEASON_TIME.has(w) && w !== 'mlb' && w !== 'game' && w !== 'games') ? rest : null;
};

/**
 * The MLB round a query names (postseasonAsk without the game).
 * @param {string[]} words
 * @returns {'world series' | 'alcs' | 'nlcs' | null}
 */
export const postseasonRound = (words) => postseasonAsk(words)?.round ?? null;

// The tables a typed word is looked up in answer to "constructor", "__proto__", "toString": own keys only
// (a plain object answers with Object.prototype's function, and the code then iterated it)
for (const table of [CITY_ALIASES, PLACE_PHRASES, LEAGUE_PHRASES, GENRE_SYNONYMS, LEAGUE_WORDS, CATEGORY_WORDS, SPORTS, SPORT_WORDS]) Object.setPrototypeOf(table, null);
